import type { ComponentType } from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import type { GridRenderCellParams } from "@mui/x-data-grid-premium";
import When from "ui/support/When";
import type { DiffColumn, HistoryRecord } from "../repository";
import useChange from "./useChange";

export const withValue = (value: string, fallback = '-') => (
    value || fallback
);

export const withChange = (Component?: ComponentType<GridRenderCellParams>) =>
    (props: GridRenderCellParams<string, HistoryRecord>) => {
        const { columnHasChange } = useChange();

        const { field, row } = props;

        const diff = row.differences?.[field as DiffColumn];

        const render = ({ value }: GridRenderCellParams) => {
            if (value && Component) {
                return (
                    <Component
                        {...props}
                    />
                );
            }

            return (
                <Box
                    sx={{
                        flex: 1,
                        height: '100%',
                        display: 'grid',
                        placeContent: 'center'
                    }}
                >
                    {withValue(value)}
                </Box>
            );
        };

        return (
            <When
                if={columnHasChange(props)}
                then={() => (
                    <Tooltip
                        arrow
                        title={`Click to see changes: ${withValue(diff.previous, '""')} -> ${withValue(diff.current, '""')}`}
                        placement='top'
                    >
                        <Box
                            sx={{
                                borderColor: '#FFAB00',
                                borderWidth: 2,
                                borderStyle: 'solid',
                                bgcolor: '#FFE4AD',
                                flex: 1,
                                height: '100%',
                                display: 'grid',
                                placeContent: 'center'
                            }}
                        >
                            {render(props)}
                        </Box>
                    </Tooltip>
                )}
                else={() => render(props)}
            />
        );
    };

export default withChange;
